import React, { useEffect, useState } from "react";
import * as api from "../../api";
import { fmt, today } from "../common/CommonComponents";

function InternalTransfers({ showAlert, user }) {
  const canCreate = !!user?.isBusinessOwner || (user?.role === "manager" && !user?.shopId);
  const [transfers, setTransfers] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ direction:"central-to-shop", shopId:"", amount:"", date:today(), notes:"" });

  const load = async () => {
    setLoading(true);
    try {
      const [rows, shopRows] = await Promise.all([
        api.getInternalTransfers(),
        canCreate ? api.getShops().catch(() => []) : Promise.resolve([]),
      ]);
      setTransfers(rows || []);
      setShops((shopRows || []).filter(s => s.active !== false));
    } catch (e) {
      showAlert(e.message, "error");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [canCreate]);

  const submit = async () => {
    if (!form.shopId || !Number(form.amount)) return showAlert("Choose a shop and enter a transfer amount.", "error");
    try {
      const centralToShop = form.direction === "central-to-shop";
      await api.createInternalTransfer({
        fromShopId: centralToShop ? null : form.shopId,
        toShopId: centralToShop ? form.shopId : null,
        amount: Number(form.amount),
        date: form.date,
        notes: form.notes,
      });
      showAlert("Internal cash transfer recorded.");
      setForm({ direction:"central-to-shop", shopId:"", amount:"", date:today(), notes:"" });
      setShowForm(false);
      load();
    } catch (e) {
      showAlert(e.message, "error");
    }
  };

  if (loading) return <div className="spinner">Loading internal transfers...</div>;

  return (
    <div>
      <div className="section-header">
        <div>
          <h2 style={{margin:0,fontSize:16,fontWeight:800}}>🔁 Internal Cash Transfers</h2>
          <p style={{fontSize:12,color:"var(--text-3)",marginTop:4}}>
            Move operating cash between Central and a shop without creating revenue, expense, or new capital.
          </p>
        </div>
        {canCreate && <button className="btn btn-primary" onClick={() => setShowForm(v => !v)}>+ New Transfer</button>}
      </div>

      {user?.shopId && (
        <div className="alert alert-info" style={{marginBottom:16,fontSize:12}}>
          You can only see transfers involving your assigned shop. Business capital, loans, and Central expenses remain outside your shop accounts.
        </div>
      )}

      {showForm && canCreate && (
        <div className="card" style={{marginBottom:18}}>
          <div className="form-grid">
            <div className="form-group">
              <label>Direction</label>
              <select value={form.direction} onChange={e => setForm(f => ({...f,direction:e.target.value}))}>
                <option value="central-to-shop">Central → Shop</option>
                <option value="shop-to-central">Shop → Central</option>
              </select>
            </div>
            <div className="form-group">
              <label>Shop</label>
              <select value={form.shopId} onChange={e => setForm(f => ({...f,shopId:e.target.value}))}>
                <option value="">Select shop</option>
                {shops.map(s => <option key={s.shopId} value={s.shopId}>{s.name}{s.state ? ` — ${s.state}` : ""}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Amount</label>
              <input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(f => ({...f,amount:e.target.value}))} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({...f,date:e.target.value}))} />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <input value={form.notes} onChange={e => setForm(f => ({...f,notes:e.target.value}))} placeholder="Purpose of transfer (optional)" />
          </div>
          <div style={{display:"flex",gap:8}}>
            <button className="btn btn-primary" onClick={submit}>Record Transfer</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Transfer</th><th>Amount</th><th>Notes</th><th>Recorded by</th></tr></thead>
            <tbody>
              {transfers.map(t => (
                <tr key={t.transferId}>
                  <td>{new Date(t.date || t.createdAt).toLocaleDateString()}</td>
                  <td><strong>{t.fromLocation}</strong> → <strong>{t.toLocation}</strong><div style={{fontSize:10,color:"var(--text-3)"}}>{t.transferId}</div></td>
                  <td>{fmt(t.amount)}</td>
                  <td>{t.notes || "—"}</td>
                  <td>{t.recordedBy}</td>
                </tr>
              ))}
              {!transfers.length && <tr><td colSpan="5" style={{textAlign:"center",padding:24,color:"var(--text-3)"}}>No internal transfers recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default InternalTransfers;
